// NOTE: `client.server` and `access-check.server` are dynamically imported
// inside each .handler() body so the service-role client cannot leak into the
// client bundle via this client-reachable `.functions.ts` module.
// See tanstack-supabase-import-graph.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

export type LabUploadRow = {
  id: string;
  user_id: string;
  lab_date: string;
  lab_name: string | null;
  estradiol_value: number | null; estradiol_unit: string | null;
  fsh_value: number | null; fsh_unit: string | null;
  lh_value: number | null; lh_unit: string | null;
  progesterone_value: number | null; progesterone_unit: string | null;
  tsh_value: number | null; tsh_unit: string | null;
  file_path: string | null;
  file_mime_type: string | null;
  created_at: string;
  updated_at: string;
};

const accessTokenSchema = z.object({ accessToken: z.string().min(10) });

// ---------- LIST ------------------------------------------------------

export const listLabUploads = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => accessTokenSchema.parse(data))
  .handler(async ({ data }): Promise<LabUploadRow[]> => {
    const { authFromAccessToken } = await import("@/server/labs/auth.server");
    const { assertActiveAccess } = await import("@/integrations/supabase/access-check.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { safeError } = await import("./safe-error.server");
    try {
      const { userId } = await authFromAccessToken(data.accessToken);
      await assertActiveAccess(userId);
      const { data: rows, error } = await supabaseAdmin
        .from("lab_uploads")
        .select("*")
        .eq("user_id", userId)
        .order("lab_date", { ascending: false });
      if (error) throw safeError(error, { scope: "labs.list", userMessage: "Couldn't load your lab uploads." });
      return (rows || []) as LabUploadRow[];
    } catch (err) {
      if (err instanceof Error && (err.message === "Not found" || err.message.startsWith("Couldn't") || err.message.startsWith("Not signed in"))) throw err;
      throw safeError(err, { scope: "labs.list", userMessage: "Couldn't load your lab uploads." });
    }
  });


// ---------- SAVE ------------------------------------------------------

const numOrNull = z.union([z.number(), z.null()]).optional();
const strOrNull = z.union([z.string().max(50), z.null()]).optional();

const saveSchema = z.object({
  lab_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "lab_date must be YYYY-MM-DD"),
  lab_name: z.string().max(120).nullable().optional(),
  estradiol_value: numOrNull, estradiol_unit: strOrNull,
  fsh_value: numOrNull,       fsh_unit: strOrNull,
  lh_value: numOrNull,        lh_unit: strOrNull,
  progesterone_value: numOrNull, progesterone_unit: strOrNull,
  tsh_value: numOrNull,       tsh_unit: strOrNull,
  // Path inside the lab-uploads bucket (e.g. "<userId>/2026-04-28-abc123.pdf").
  file_path: z.string().max(512).nullable().optional(),
  file_mime_type: z.string().max(100).nullable().optional(),
});

export const saveLabUpload = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => saveSchema.merge(accessTokenSchema).parse(data))
  .handler(async ({ data }): Promise<LabUploadRow> => {
    const { authFromAccessToken } = await import("@/server/labs/auth.server");
    const { assertActiveAccess } = await import("@/integrations/supabase/access-check.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { safeError } = await import("./safe-error.server");
    const { userId } = await authFromAccessToken(data.accessToken);
    await assertActiveAccess(userId);
    if (data.file_path && !data.file_path.startsWith(`${userId}/`)) {
      throw safeError(new Error("file_path mismatch"), {
        scope: "labs.save",
        userMessage: "We couldn't save that file. Please try uploading again.",
        context: { userId },
      });
    }
    const row = {
      user_id: userId,
      lab_date: data.lab_date,
      lab_name: data.lab_name ?? null,
      estradiol_value: data.estradiol_value ?? null,
      estradiol_unit: data.estradiol_unit ?? "pg/mL",
      fsh_value: data.fsh_value ?? null,
      fsh_unit: data.fsh_unit ?? "mIU/mL",
      lh_value: data.lh_value ?? null,
      lh_unit: data.lh_unit ?? "mIU/mL",
      progesterone_value: data.progesterone_value ?? null,
      progesterone_unit: data.progesterone_unit ?? "ng/mL",
      tsh_value: data.tsh_value ?? null,
      tsh_unit: data.tsh_unit ?? "µIU/mL",
      file_path: data.file_path ?? null,
      file_mime_type: data.file_mime_type ?? null,
    };
    const { data: inserted, error } = await supabaseAdmin
      .from("lab_uploads")
      .insert(row)
      .select("*")
      .single();
    if (error) throw safeError(error, { scope: "labs.save", userMessage: "Couldn't save your lab result." });
    return inserted as LabUploadRow;
  });

// ---------- DELETE ----------------------------------------------------

export const deleteLabUpload = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => accessTokenSchema.extend({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data }) => {
    const { authFromAccessToken } = await import("@/server/labs/auth.server");
    const { assertActiveAccess } = await import("@/integrations/supabase/access-check.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { safeError, notFoundError } = await import("./safe-error.server");
    const { userId } = await authFromAccessToken(data.accessToken);
    await assertActiveAccess(userId);
    const { data: row, error: rErr } = await supabaseAdmin
      .from("lab_uploads")
      .select("id, user_id, file_path")
      .eq("id", data.id)
      .maybeSingle();
    if (rErr) throw safeError(rErr, { scope: "labs.delete", userMessage: "Couldn't delete that lab." });
    if (!row || row.user_id !== userId) throw notFoundError("labs.delete");

    const { error: delErr } = await supabaseAdmin.from("lab_uploads").delete().eq("id", data.id);
    if (delErr) throw safeError(delErr, { scope: "labs.delete", userMessage: "Couldn't delete that lab." });

    if (row.file_path) {
      await supabaseAdmin.storage.from("lab-uploads").remove([row.file_path]).catch(() => {});
    }
    return { ok: true };
  });

// ---------- SIGNED URL FOR ORIGINAL FILE -----------------------------

export const getLabFileUrl = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => accessTokenSchema.extend({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data }) => {
    const { authFromAccessToken } = await import("@/server/labs/auth.server");
    const { assertActiveAccess } = await import("@/integrations/supabase/access-check.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { safeError, notFoundError } = await import("./safe-error.server");
    const { userId } = await authFromAccessToken(data.accessToken);
    await assertActiveAccess(userId);
    const { data: row, error } = await supabaseAdmin
      .from("lab_uploads")
      .select("id, user_id, file_path")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw safeError(error, { scope: "labs.fileUrl", userMessage: "Couldn't open that file." });
    if (!row || row.user_id !== userId || !row.file_path) throw notFoundError("labs.fileUrl");
    const { data: signed, error: sErr } = await supabaseAdmin.storage
      .from("lab-uploads")
      .createSignedUrl(row.file_path, 60 * 10);
    if (sErr || !signed?.signedUrl) {
      throw safeError(sErr ?? new Error("no signed url"), {
        scope: "labs.fileUrl",
        userMessage: "Couldn't open that file.",
      });
    }
    return { url: signed.signedUrl };
  });

